import { TablePreviewModal } from '@/components/admin/data-sources/TablePreviewModal';
import { CopyButton } from '@/components/ui/copy-button';
import { cn } from '@/lib/cn';
import type { DataSourceTable } from '@innermate/shared';
import { ChevronRight, Eye, ScanEye, Search, Table2 } from 'lucide-react';
import { useMemo, useState } from 'react';

interface TableTreeProps {
  /** 데이터소스 id — 미리보기 API 호출 시 사용. */
  dataSourceId: string;
  tables: DataSourceTable[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function TableTree({ dataSourceId, tables, selectedId, onSelect }: TableTreeProps) {
  const [previewTable, setPreviewTable] = useState<{ id: string; name: string } | null>(null);
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const filtered = query.trim()
      ? tables.filter((t) =>
          `${t.schemaName}.${t.tableName}`.toLowerCase().includes(query.toLowerCase()),
        )
      : tables;
    const map = new Map<string, DataSourceTable[]>();
    for (const t of filtered) {
      const arr = map.get(t.schemaName) ?? [];
      arr.push(t);
      map.set(t.schemaName, arr);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [tables, query]);

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  return (
    <aside className="w-[280px] shrink-0 border-r border-border h-full overflow-hidden flex flex-col">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-foreground-muted" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="테이블 검색…"
            className={cn(
              'h-8 w-full rounded-md bg-surface-muted/40 border border-transparent pl-7 pr-2 text-xs',
              'focus-visible:outline-none focus-visible:border-border',
            )}
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-2 px-2">
        {grouped.length === 0 ? (
          <p className="text-xs text-foreground-muted px-2 py-3">테이블이 없습니다.</p>
        ) : (
          grouped.map(([schema, items]) => {
            const isCollapsed = collapsed.has(schema);
            return (
              <div key={schema} className="mb-2">
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(collapsed);
                    if (isCollapsed) next.delete(schema);
                    else next.add(schema);
                    setCollapsed(next);
                  }}
                  className="flex w-full items-center gap-1 px-2 h-7 text-xs text-foreground-muted hover:text-foreground"
                >
                  <ChevronRight
                    className={cn('size-3 transition-transform', !isCollapsed && 'rotate-90')}
                  />
                  <span className="font-medium uppercase tracking-wider">{schema}</span>
                  <span className="ml-auto">{items.length}</span>
                </button>
                {!isCollapsed
                  ? items.map((t) => {
                      const isActive = t.id === selectedId;
                      const isRemoved = !!t.removedAt;
                      return (
                        <div
                          key={t.id}
                          className={cn(
                            'group relative flex items-center rounded-md',
                            isActive
                              ? 'bg-surface-muted text-foreground'
                              : 'text-foreground-muted hover:bg-surface-muted/40 hover:text-foreground',
                            isRemoved && 'opacity-50',
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => onSelect(t.id)}
                            className={cn(
                              'flex min-w-0 flex-1 items-center gap-2 px-2 h-8 text-xs rounded-md',
                              isRemoved && 'line-through',
                            )}
                            title={isRemoved ? '마지막 sync 에서 발견되지 않음' : undefined}
                          >
                            {t.isView ? (
                              <Eye className="size-3.5 shrink-0" />
                            ) : (
                              <Table2 className="size-3.5 shrink-0" />
                            )}
                            <span className="truncate">{t.tableName}</span>
                          </button>
                          <span
                            className={cn(
                              'ml-auto pr-2 text-foreground-muted/80 text-xs',
                              'group-hover:hidden group-focus-within:hidden',
                            )}
                          >
                            {t.columnCount}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewTable({ id: t.id, name: t.tableName });
                            }}
                            aria-label={`${t.tableName} 미리보기`}
                            title="미리보기 (1000행)"
                            className={cn(
                              'mr-0.5 inline-grid size-6 place-items-center rounded text-foreground-muted',
                              'hover:bg-surface-muted hover:text-foreground',
                              'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                            )}
                          >
                            <ScanEye className="size-3.5" />
                          </button>
                          <CopyButton
                            text={t.tableName}
                            label={`${t.tableName} 복사`}
                            className={cn(
                              'mr-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                            )}
                          />
                        </div>
                      );
                    })
                  : null}
              </div>
            );
          })
        )}
      </div>
      {previewTable ? (
        <TablePreviewModal
          dataSourceId={dataSourceId}
          tableId={previewTable.id}
          tableName={previewTable.name}
          open={true}
          onClose={() => setPreviewTable(null)}
        />
      ) : null}
    </aside>
  );
}
